import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { ReplyToMessageContext } from '@components/ReplyToMessageContext/ReplyToMessageContext'
import { RiverTimelineEvent } from '@towns-protocol/sdk'
import { useChannelId, useThrottledTimelineStore, useUserLookupContext } from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

const NULL_PREVIOUS_EVENT = { event: undefined, eventContent: undefined }

export const useInlineReplyAttchmentPreview = (params?: { onNewInlineReply?: () => void }) => {
    const { replyToEventId, setReplyToEventId } = useContext(ReplyToMessageContext)

    const cancelInlineReply = useCallback(() => {
        setReplyToEventId?.(null)
    }, [setReplyToEventId])

    // assuming inline replies are always tied to the current channel
    const channelId = useChannelId()

    // lookup message content to create preview
    const { event, eventContent } = useThrottledTimelineStore(
        useCallback(
            (state) => {
                if (replyToEventId) {
                    const event = state.timelines[channelId]?.find(
                        (e) => e.eventId === replyToEventId,
                    )
                    if (event?.content?.kind === RiverTimelineEvent.ChannelMessage) {
                        return {
                            event,
                            eventContent: event.content,
                        }
                    }
                }
                return NULL_PREVIOUS_EVENT
            },
            [replyToEventId, channelId],
        ),
    )

    const { lookupUser } = useUserLookupContext()
    const inlineReply = useMemo(() => {
        if (event && eventContent) {
            return {
                event,
                eventContent,
                displayName: getPrettyDisplayName(lookupUser(event.sender.id)),
            }
        }
    }, [lookupUser, event, eventContent])

    const onNewInlineReplyRef = useRef(params?.onNewInlineReply)
    onNewInlineReplyRef.current = params?.onNewInlineReply

    useEffect(() => {
        if (replyToEventId && onNewInlineReplyRef.current) {
            onNewInlineReplyRef.current()
        }
    }, [replyToEventId])

    return { inlineReplyPreview: inlineReply, onCancelInlineReply: cancelInlineReply }
}
